import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Avatar,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  IconButton,
  Tooltip,
  Card,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  EventNote as EventNoteIcon,
} from '@mui/icons-material';
import { getScores, getScoreById, updateScore, deleteScore, getPartnerAdminScores, updatePartnerAdminScore } from '../../api/adminApi';
import { useToast } from '../../contexts/ToastContext';
import { useAdmin } from '../../contexts/AdminContext';
import ModernButton from '../../components/common/ModernButton';
import ModernTextField from '../../components/common/ModernTextField';
import GroupedAccordionList from '../../components/common/GroupedAccordionList';
import useGroupedData from '../../hooks/useGroupedData';

const ScoresManagement = () => {
  const [scores, setScores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewDialog, setViewDialog] = useState(null);
  const [editDialog, setEditDialog] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [deleteDialog, setDeleteDialog] = useState(null);
  const { showToast } = useToast();
  const { isPlatformAdmin } = useAdmin();
  // All authenticated admin users (platform admin or partner admin) can edit scores.
  // Partner admins are restricted to Writing and Speaking fields only (enforced below).
  const canEditScores = true;

  const fetchScores = useCallback(async () => {
    setLoading(true);
    try {
      // Use partner-scoped API for non-platform admins
      const { data } = isPlatformAdmin
        ? await getScores({ limit: 1000 })
        : await getPartnerAdminScores({ limit: 1000 });
      // Handle both response formats
      setScores(data.data?.scores || data.scores || data.data || []);
    } catch (error) {
      console.error('Failed to fetch scores:', error);
      showToast('Failed to fetch scores', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast, isPlatformAdmin]);

  useEffect(() => {
    fetchScores();
  }, [fetchScores]);

  // Group scores by exam
  const {
    groupsArray,
    isGroupExpanded,
    toggleGroup,
  } = useGroupedData({
    data: scores,
    getGroupKey: (score) => score.product?._id || 'no-exam',
    getGroupMeta: (score) => ({
      examName: score.product?.name || score.product?.label || 'Unknown Exam',
      examDate: score.product?.startDate,
    }),
    itemsKey: 'scores',
  });

  const handleViewScore = (score) => {
    setViewDialog(score);
  };

  const handleEditScore = (score) => {
    setEditForm({
      _id: score._id,
      readingScore: score.readingScore ?? '',
      listeningScore: score.listeningScore ?? '',
      speakingScore: score.speakingScore ?? '',
      writingQ1: score.writingScore?.question1 ?? '',
      writingQ2: score.writingScore?.question2 ?? '',
      overAllScore: score.overAllScore ?? '',
    });
    setEditDialog(true);
  };

  const handleSaveScore = async () => {
    try {
      const { _id, writingQ1, writingQ2, readingScore, listeningScore, speakingScore, overAllScore } = editForm;

      // Convert empty strings to null for Decimal128 fields
      const parseScore = (value) => {
        if (value === '' || value === null || value === undefined) return null;
        const parsed = parseFloat(value);
        return isNaN(parsed) ? null : parsed;
      };

      let updateData;
      if (isPlatformAdmin) {
        // Platform admins can update all fields
        updateData = {
          readingScore: parseScore(readingScore),
          listeningScore: parseScore(listeningScore),
          speakingScore: parseScore(speakingScore),
          overAllScore: parseScore(overAllScore),
          writingScore: {
            question1: parseScore(writingQ1),
            question2: parseScore(writingQ2),
          },
        };
        await updateScore(_id, updateData);
      } else {
        // Partner admins: Writing and Speaking only
        updateData = {
          speakingScore: parseScore(speakingScore),
          writingScore: {
            question1: parseScore(writingQ1),
            question2: parseScore(writingQ2),
          },
        };
        await updatePartnerAdminScore(_id, updateData);
      }

      showToast('Score updated successfully', 'success');
      setEditDialog(null);
      fetchScores();
    } catch (error) {
      showToast('Failed to update score', 'error');
    }
  };

  const handleDeleteScore = async () => {
    if (!deleteDialog) return;
    try {
      await deleteScore(deleteDialog._id);
      showToast('Score deleted successfully', 'success');
      setDeleteDialog(null);
      fetchScores();
    } catch (error) {
      showToast('Failed to delete score', 'error');
    }
  };

  const getStudentName = (score) => {
    const user = score.user;
    if (user?.firstName || user?.lastName) {
      return `${user.firstName || ''} ${user.lastName || ''}`.trim();
    }
    return 'Unknown Student';
  };

  const formatScore = (value) => {
    if (value === null || value === undefined) return 'N/A';
    return typeof value === 'number' ? value.toFixed(1) : value;
  };

  const renderScoreItem = (score, index) => (
    <ListItem
      key={score._id}
      divider={index < scores.length - 1}
      sx={{
        py: 2,
        px: 3,
        '&:hover': { backgroundColor: 'action.hover' },
      }}
    >
      <ListItemText
        primary={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="subtitle2" fontWeight={600}>
              {getStudentName(score)}
            </Typography>
            {score.overAllScore && (
              <Chip
                label={`Overall: ${formatScore(score.overAllScore)}`}
                size="small"
                color="success"
                sx={{ fontWeight: 700 }}
              />
            )}
          </Box>
        }
        secondary={
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
            <Chip
              label={`R: ${formatScore(score.readingScore)}`}
              size="small"
              variant="outlined"
              color={score.readingScore ? 'primary' : 'default'}
            />
            <Chip
              label={`L: ${formatScore(score.listeningScore)}`}
              size="small"
              variant="outlined"
              color={score.listeningScore ? 'primary' : 'default'}
            />
            <Chip
              label={`W: ${formatScore(score.writingScore?.overAll)}`}
              size="small"
              variant="outlined"
              color={score.writingScore?.overAll ? 'primary' : 'default'}
            />
            <Chip
              label={`S: ${formatScore(score.speakingScore)}`}
              size="small"
              variant="outlined"
              color={score.speakingScore ? 'primary' : 'default'}
            />
          </Box>
        }
      />
      <ListItemSecondaryAction>
        <Tooltip title="View Details">
          <IconButton size="small" onClick={() => handleViewScore(score)}>
            <ViewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {canEditScores && (
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => handleEditScore(score)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {isPlatformAdmin && (
          <Tooltip title="Delete">
            <IconButton size="small" color="error" onClick={() => setDeleteDialog(score)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </ListItemSecondaryAction>
    </ListItem>
  );

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar sx={{ backgroundColor: 'success.main', width: 48, height: 48, mr: 2 }}>
            <AssessmentIcon />
          </Avatar>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600 }}>
              Scores Management
            </Typography>
            <Typography variant="body1" color="text.secondary">
              View and manage all exam scores grouped by exam
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Grouped Accordion List */}
      <GroupedAccordionList
        groups={groupsArray}
        itemsKey="scores"
        isGroupExpanded={isGroupExpanded}
        onToggleGroup={toggleGroup}
        renderItem={renderScoreItem}
        groupIcon={EventNoteIcon}
        itemLabel="score"
        loading={loading}
        emptyState={
          <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
            <AssessmentIcon sx={{ fontSize: 64, mb: 2, opacity: 0.3 }} />
            <Typography variant="h6">No scores found</Typography>
            <Typography variant="body2">
              Scores will appear here once exams are completed and graded.
            </Typography>
          </Box>
        }
      />

      {/* View Dialog */}
      {viewDialog && (
        <Dialog open={!!viewDialog} onClose={() => setViewDialog(null)} maxWidth="sm" fullWidth>
          <DialogTitle>Score Details</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <Card variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                    Section Scores
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Reading</Typography>
                      <Typography variant="h6" fontWeight={700}>
                        {viewDialog.readingScore?.toString() || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Listening</Typography>
                      <Typography variant="h6" fontWeight={700}>
                        {viewDialog.listeningScore?.toString() || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Writing</Typography>
                      <Typography variant="h6" fontWeight={700}>
                        {viewDialog.writingScore?.overAll?.toString() || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Speaking</Typography>
                      <Typography variant="h6" fontWeight={700}>
                        {viewDialog.speakingScore?.toString() || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">Overall Score</Typography>
                      <Typography variant="h4" fontWeight={700} color="success.main">
                        {viewDialog.overAllScore?.toString() || 'N/A'}
                      </Typography>
                    </Grid>
                  </Grid>
                </Card>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <ModernButton onClick={() => setViewDialog(null)}>Close</ModernButton>
          </DialogActions>
        </Dialog>
      )}

      {/* Edit Dialog */}
      {editDialog && (
        <Dialog open={editDialog} onClose={() => setEditDialog(null)} maxWidth="sm" fullWidth>
          <DialogTitle>Edit Score</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {isPlatformAdmin && (
                <>
                  <Grid item xs={6}>
                    <ModernTextField
                      fullWidth
                      label="Reading Score"
                      type="number"
                      inputProps={{ step: 0.5, min: 0, max: 9 }}
                      value={editForm.readingScore}
                      onChange={(e) => setEditForm({ ...editForm, readingScore: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <ModernTextField
                      fullWidth
                      label="Listening Score"
                      type="number"
                      inputProps={{ step: 0.5, min: 0, max: 9 }}
                      value={editForm.listeningScore}
                      onChange={(e) => setEditForm({ ...editForm, listeningScore: e.target.value })}
                    />
                  </Grid>
                </>
              )}
              <Grid item xs={6}>
                <ModernTextField
                  fullWidth
                  label="Writing Q1"
                  type="number"
                  inputProps={{ step: 0.5, min: 0, max: 9 }}
                  value={editForm.writingQ1}
                  onChange={(e) => setEditForm({ ...editForm, writingQ1: e.target.value })}
                />
              </Grid>
              <Grid item xs={6}>
                <ModernTextField
                  fullWidth
                  label="Writing Q2"
                  type="number"
                  inputProps={{ step: 0.5, min: 0, max: 9 }}
                  value={editForm.writingQ2}
                  onChange={(e) => setEditForm({ ...editForm, writingQ2: e.target.value })}
                />
              </Grid>
              <Grid item xs={6}>
                <ModernTextField
                  fullWidth
                  label="Speaking Score"
                  type="number"
                  inputProps={{ step: 0.5, min: 0, max: 9 }}
                  value={editForm.speakingScore}
                  onChange={(e) => setEditForm({ ...editForm, speakingScore: e.target.value })}
                />
              </Grid>
              {isPlatformAdmin && (
                <Grid item xs={6}>
                  <ModernTextField
                    fullWidth
                    label="Overall Score"
                    type="number"
                    inputProps={{ step: 0.5, min: 0, max: 9 }}
                    value={editForm.overAllScore}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <ModernButton onClick={() => setEditDialog(null)}>Cancel</ModernButton>
            <ModernButton variant="contained" onClick={handleSaveScore}>
              Save Changes
            </ModernButton>
          </DialogActions>
        </Dialog>
      )}

      {/* Delete Dialog */}
      {deleteDialog && (
        <Dialog open={!!deleteDialog} onClose={() => setDeleteDialog(null)}>
          <DialogTitle>Delete Score?</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete the score for <strong>{getStudentName(deleteDialog)}</strong>? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <ModernButton onClick={() => setDeleteDialog(null)}>Cancel</ModernButton>
            <ModernButton variant="contained" color="error" onClick={handleDeleteScore}>
              Delete
            </ModernButton>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default ScoresManagement;
